import { z } from 'zod';
import { lenientString, lenientUuid, lenientDateString, lenientNumber, lenientOptionalNode } from '../schema-extensions';
import { PetType } from '../enums/pet-type.enum';
/**
 * Validation constants for PetNode
 * Defined before schemas to avoid forward reference issues
 */
const PET_CONSTANTS = {
  NAME_MAX_LENGTH: 100,
  DATE_FORMAT_LENGTH: 10,
  TYPE_OTHER_MAX_LENGTH: 200,
} as const;

/**
 * Lenient schema for deserializing PetNode from storage
 * Handles missing fields and data migrations gracefully
 */
const PetNodeDeserializeSchema = z.object({
  id: lenientUuid(),
  name: lenientString(PET_CONSTANTS.NAME_MAX_LENGTH, ''),
  dateOfBirth: lenientDateString(''),
  type: z.nativeEnum(PetType).optional().catch(undefined),
  typeOther: z.string().max(PET_CONSTANTS.TYPE_OTHER_MAX_LENGTH).optional().catch(undefined),
  archiveDate: z.string().optional().catch(undefined).transform(val => val?.slice(0, 10))
}).transform((val): PetNode => {
  const base = { id: val.id, name: val.name, dateOfBirth: val.dateOfBirth, archiveDate: val.archiveDate };
  if (val.type === PetType.OTHER) {
    return { ...base, type: PetType.OTHER, typeOther: val.typeOther ?? '' };
  }
  return val.type ? { ...base, type: val.type } : { ...base };
});

/**
 * Strict schema for creating new PetNode
 * Enforces validation rules for new data
 */
const PetNodeCreateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Name is required').max(PET_CONSTANTS.NAME_MAX_LENGTH),
  dateOfBirth: z.string().length(PET_CONSTANTS.DATE_FORMAT_LENGTH, 'Date must be in YYYY-MM-DD format'),
  type: z.nativeEnum(PetType).optional(),
  typeOther: z.string().max(PET_CONSTANTS.TYPE_OTHER_MAX_LENGTH).optional(),
  archiveDate: z.string().length(PET_CONSTANTS.DATE_FORMAT_LENGTH, 'Date must be in YYYY-MM-DD format').optional(),
}).superRefine((val, ctx) => {
  if (val.type === PetType.OTHER && !val.typeOther) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'typeOther is required when type is Other',
      path: ['typeOther'],
    });
  }
}).transform((val): PetNode => {
  const base = { id: val.id, name: val.name, dateOfBirth: val.dateOfBirth, archiveDate: val.archiveDate };
  if (val.type === PetType.OTHER) {
    return { ...base, type: PetType.OTHER, typeOther: val.typeOther! };
  }
  return val.type ? { ...base, type: val.type } : { ...base };
});

/**
 * PetNode type
 * Represents a pet in the vault (dog, cat, etc.).
 * When type is PetType.OTHER, typeOther must be provided to describe the pet type.
 */
export type PetNode =
  | { id: string; name: string; dateOfBirth: string; archiveDate?: string; type: PetType.OTHER; typeOther: string }
  | { id: string; name: string; dateOfBirth: string; archiveDate?: string; type?: Exclude<PetType, PetType.OTHER> };

/**
 * PetNode namespace with factory and deserialization methods
 */
export namespace PetNode {
  /**
   * Validation constants for PetNode
   */
  export const Constants = PET_CONSTANTS;

  /**
   * Deserialize PetNode from unknown data (e.g., from storage)
   * Uses lenient validation with automatic defaults for missing fields
   * 
   * @param data Unknown data to deserialize
   * @returns Valid PetNode with defaults applied
   */
  export function deserialize(data: unknown): PetNode {
    return PetNodeDeserializeSchema.parse(data);
  }

  /**
   * Safely deserialize PetNode with error handling
   * 
   * @param data Unknown data to deserialize
   * @returns Success result with data or failure result with error
   */
  export function deserializeSafe(data: unknown): 
    | { success: true; data: PetNode }
    | { success: false; error: z.ZodError } 
  {
    const result = PetNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  /**
   * Create a new PetNode with strict validation
   * Use this for creating new pets from user input
   * 
   * @param data Partial pet data
   * @returns Valid PetNode
   * @throws ZodError if validation fails
   */
  export function create(data: Partial<PetNode>): PetNode {
    return PetNodeCreateSchema.parse({
      id: data.id ?? crypto.randomUUID(),
      ...data
    });
  }

  /**
   * Validate if data conforms to PersonNode structure
   * 
   * @param data Data to validate
   * @returns True if data is a valid PersonNode
   */
  export function validate(data: unknown): data is PetNode {
    return PetNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Calculate age based on date of birth
   * 
   * @param node PersonNode to calculate age for
   * @param referenceDate Date to calculate age at (defaults to today)
   * @returns Age in years
   */
  export function calculateAge(node: PetNode, referenceDate: Date = new Date()): number {
    if (!node.dateOfBirth) return 0;
    
    const birthDate = new Date(node.dateOfBirth);
    let age = referenceDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = referenceDate.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }

  export function isActive(node: PetNode, referenceDate: Date = new Date()): boolean {
    return !node.archiveDate || new Date(node.archiveDate) > referenceDate;
  }
}
